import { apiClient } from "@/shared/services/api";

type ApiResponse<T> = { success: boolean; data: T; message?: string };
const unwrap = <T>(response: { data: ApiResponse<T> }) => response.data.data;

export const DirectorsReportFrequency = {
  Daily: 1,
  Weekly: 2,
  Monthly: 3,
  Quarterly: 4,
  SemiAnnual: 5,
  Annual: 6,
  Custom: 7,
} as const;
export type DirectorsReportFrequency = typeof DirectorsReportFrequency[keyof typeof DirectorsReportFrequency];

export const DirectorsReportDeliveryType = {
  To: 1,
  Cc: 2,
  Bcc: 3,
} as const;
export type DirectorsReportDeliveryType = typeof DirectorsReportDeliveryType[keyof typeof DirectorsReportDeliveryType];

export const DirectorsReportPeriodRule = {
  PreviousCompletedWorkingDay: 1,
  PreviousMondayToSunday: 2,
  CustomRange: 99,
} as const;
export type DirectorsReportPeriodRule = typeof DirectorsReportPeriodRule[keyof typeof DirectorsReportPeriodRule];

export const DirectorsReportSection = {
  ExecutiveSummary: 1,
  SalesPerformance: 2,
  PurchasesAndPayables: 3,
  OvergrownSeedlings: 4,
  SalesTrendGraph: 5,
  PurchasesTrendGraph: 6,
  OvergrownTrendGraph: 7,
  CompanyPerformance: 8,
  SeedlingsMovement: 9,
} as const;
export type DirectorsReportSection = typeof DirectorsReportSection[keyof typeof DirectorsReportSection];

export interface DirectorsReportSettingsDto {
  id: string;
  companyId: string;
  automatedReportingEnabled: boolean;
  businessTimezone: string;
  currency: string;
  financialYearStartMonth: number;
  workingDays: number[];
  defaultReportFormat: number;
  reportRetentionDays: number;
  reportDisplayNameOverride?: string | null;
  emailSenderDisplayName?: string | null;
  reportTitleTemplate: string;
  emailSubjectTemplate: string;
  emailHeadingTemplate: string;
  fileNameTemplate: string;
  includeCompanyLogo: boolean;
  dateFormat: string;
  brandingPreview: DirectorsReportBrandingPreviewDto;
}

export interface DirectorsReportBrandingPreviewDto {
  companyName: string;
  legalName: string;
  tradingName: string;
  periodLabel: string;
  emailSubject: string;
  pdfHeading: string;
  emailHeading: string;
  attachmentFileName: string;
  emailSenderDisplayName: string;
}

export type UpdateDirectorsReportSettingsDto = Omit<DirectorsReportSettingsDto, "id" | "companyId" | "brandingPreview">;

export interface DirectorsReportScheduleDto {
  id: string;
  companyId: string;
  scheduleName: string;
  frequency: DirectorsReportFrequency;
  isEnabled: boolean;
  deliveryTime: string;
  deliveryDay: number | null;
  reportingPeriodRule: DirectorsReportPeriodRule;
  compareWithPreviousPeriod: boolean;
  attachPdf: boolean;
  includeEmailSummary: boolean;
  enabledSections: DirectorsReportSection[];
  reportVersion: number;
}

export interface DirectorsReportRecipientDto {
  id: string;
  companyId: string;
  name: string;
  emailAddress: string;
  isActive: boolean;
  deliveryType: DirectorsReportDeliveryType;
  receivePdf: boolean;
  receiveEmailSummary: boolean;
  scheduleIds: string[];
  lastSuccessfulDeliveryAt?: string | null;
  lastFailedDeliveryReason?: string | null;
}

export interface DirectorsReportSummaryDto {
  id: string;
  companyId: string;
  scheduleId?: string | null;
  reportName: string;
  reportType: DirectorsReportFrequency;
  periodStart: string;
  periodEnd: string;
  periodEndIsExclusive: boolean;
  periodLabel: string;
  generationSource: string;
  generationStatus: string;
  deliveryStatus: string;
  recipientCount: number;
  deliveredCount: number;
  failedCount: number;
  pendingCount: number;
  lastDeliveryAttemptAt?: string | null;
  lastDeliveryFailureReason?: string | null;
  hasFile: boolean;
  fileSizeBytes?: number | null;
  status: number;
  generatedAt?: string | null;
  fileName: string;
  errorMessage?: string | null;
}

export interface DirectorsReportDetailDto extends DirectorsReportSummaryDto {
  businessTimezone: string;
  summaryJson?: string | null;
  metricSnapshotJson?: string | null;
  deliveries: DirectorsReportDeliveryDto[];
}

export interface SalesPeriodMetricsDto {
  period: { from: string; through: string; fromUtc: string; toExclusiveUtc: string };
  grossSalesAmount: number;
  pieceCount: number;
  trayEquivalent: number;
  unresolvedPieceCount: number;
  unresolvedLineCount: number;
  orderCount: number;
}

export interface SalesTrendDto {
  currentValue: number;
  previousValue: number;
  percentageChange?: number | null;
  direction: number | string;
  isComparable: boolean;
}

export interface SalesPerformanceReportDto {
  asOf: string;
  businessTimezone: string;
  daily: SalesPeriodMetricsDto;
  weekly: SalesPeriodMetricsDto;
  monthly: SalesPeriodMetricsDto;
  yearToDate: SalesPeriodMetricsDto;
  trend: { daily: SalesTrendDto; weekly: SalesTrendDto; monthly: SalesTrendDto; yearToDate: SalesTrendDto };
  breakdown: SalesPerformanceBreakdownDto;
  weeklyDailySeries: SalesSeriesPointDto[];
  monthlyDailySeries: SalesSeriesPointDto[];
  yearComparison: SalesYearComparisonPointDto[];
  dataQuality: { unresolvedLineCount: number; unresolvedPieceCount: number; warnings: string[] };
  periodLabels: ReportingPeriodLabelsDto;
  trayTrend: { daily: SalesTrendDto; weekly: SalesTrendDto; monthly: SalesTrendDto; yearToDate: SalesTrendDto };
}

export interface ReportingPeriodLabelsDto {
  daily: string;
  weekly: string;
  monthly: string;
  yearToDate: string;
  monthlyTrend: string;
}

export interface SalesSeriesPointDto {
  date: string;
  grossSalesAmount: number;
  orderCount: number;
}

export interface SalesYearComparisonPointDto {
  month: number;
  currentYearAmount?: number | null;
  previousYearAmount?: number | null;
}

export interface SalesSellingUnitBreakdownDto {
  pricingMode: number;
  unit: number;
  trayType?: number | null;
  trayCapacity?: number | null;
  grossSalesAmount: number;
  pieceCount: number;
  trayEquivalent: number;
  unresolvedPieceCount: number;
  unresolvedLineCount: number;
}

export interface SalesVarietyBreakdownDto {
  varietyName: string;
  grossSalesAmount: number;
  pieceCount: number;
  trayEquivalent: number;
  unresolvedPieceCount: number;
  unresolvedLineCount: number;
  sellingUnits: SalesSellingUnitBreakdownDto[];
}

export interface SalesCropBreakdownDto {
  cropName: string;
  grossSalesAmount: number;
  pieceCount: number;
  trayEquivalent: number;
  unresolvedPieceCount: number;
  unresolvedLineCount: number;
  varieties: SalesVarietyBreakdownDto[];
}

export interface SalesPeriodBreakdownDto {
  crops: SalesCropBreakdownDto[];
  grossSalesAmount: number;
  pieceCount: number;
  trayEquivalent: number;
  unresolvedPieceCount: number;
  unresolvedLineCount: number;
}

export interface SalesPerformanceBreakdownDto {
  daily: SalesPeriodBreakdownDto;
  weekly: SalesPeriodBreakdownDto;
  monthly: SalesPeriodBreakdownDto;
  yearToDate: SalesPeriodBreakdownDto;
}

export const CompanyPerformanceMetricAvailability = {
  Available: 1,
  Partial: 2,
  NotTracked: 3,
} as const;

export interface CompanyPerformancePeriodValueDto {
  period: { from: string; through: string; fromUtc: string; toExclusiveUtc: string };
  pieceCount?: number | null;
  trayEquivalent?: number | null;
  monetaryValue?: number | null;
  eventCount: number;
  availability: number;
  note?: string | null;
}

export interface CompanyPerformanceMetricDto {
  code: string;
  label: string;
  displayOrder: number;
  daily: CompanyPerformancePeriodValueDto;
  weekly: CompanyPerformancePeriodValueDto;
  monthly: CompanyPerformancePeriodValueDto;
  yearToDate: CompanyPerformancePeriodValueDto;
  trend: SalesTrendDto;
  trendBasis: string;
  note?: string | null;
  dailyBreakdown: CompanyPerformanceBreakdownDto[];
}

export interface CompanyPerformanceBreakdownDto {
  label: string;
  pieceCount: number;
  trayEquivalent: number;
  monetaryValue: number;
}

export interface CompanyPerformanceReportDto {
  asOf: string;
  businessTimezone: string;
  currency: string;
  metrics: CompanyPerformanceMetricDto[];
  dataQualityWarnings: string[];
  periodLabels: ReportingPeriodLabelsDto;
}

export interface SeedlingsMovementRowDto {
  sowingInstructionId: string;
  batchReference: string;
  productCategory: string;
  productName: string;
  dateSown?: string | null;
  traysSown: number;
  traysGerminated: number;
  traysTransferredToGreenhouse?: number | null;
  greenhouseLocations: string;
  traysUtilisedForGapping?: number | null;
  traysAvailableForSale?: number | null;
  traysSold?: number | null;
  traysDispatched?: number | null;
  traysInGreenhouse?: number | null;
  traysInGreenhouseUnsold?: number | null;
  traysAtRiskOvergrownUnsold?: number | null;
  traysOvergrownReadyForDestruction?: number | null;
  traysDestroyed?: number | null;
  destructionCertificateNumber?: string | null;
  hasUnresolvedQuantity: boolean;
}

export interface SeedlingsMovementReportDto {
  from: string;
  through: string;
  businessTimezone: string;
  rows: SeedlingsMovementRowDto[];
  dataQualityWarnings: string[];
}

export interface DirectorsReportDeliveryDto {
  id: string;
  reportId: string;
  recipientName: string;
  recipientEmail: string;
  deliveryType: DirectorsReportDeliveryType;
  deliveryStatus: number;
  attemptedAt: string;
  deliveredAt?: string | null;
  retryNumber: number;
  failureReason?: string | null;
}

export interface DirectorsReportSendResultDto {
  reportId: string;
  status: string;
  recipientCount: number;
  deliveredCount: number;
  failedCount: number;
  pendingCount: number;
  message: string;
  deliveries: DirectorsReportDeliveryDto[];
}

const parseFileName = (contentDisposition?: string) => {
  if (!contentDisposition) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8?.[1]) return decodeURIComponent(utf8[1].replace(/"/g, ""));
  const ascii = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return ascii?.[1] ?? null;
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const directorsReportsApi = {
  getSettings: async (companyId: string) =>
    unwrap<DirectorsReportSettingsDto>(await apiClient.get("/directors-reports/settings", { params: { companyId } })),
  updateSettings: async (companyId: string, dto: UpdateDirectorsReportSettingsDto) =>
    unwrap<DirectorsReportSettingsDto>(await apiClient.put("/directors-reports/settings", dto, { params: { companyId } })),
  getSchedules: async (companyId: string) =>
    unwrap<DirectorsReportScheduleDto[]>(await apiClient.get("/directors-reports/schedules", { params: { companyId } })),
  saveSchedule: async (companyId: string, dto: Omit<DirectorsReportScheduleDto, "id" | "companyId" | "reportVersion">, id?: string) =>
    unwrap<DirectorsReportScheduleDto>(
      id
        ? await apiClient.put(`/directors-reports/schedules/${id}`, dto, { params: { companyId } })
        : await apiClient.post("/directors-reports/schedules", dto, { params: { companyId } })
    ),
  getRecipients: async (companyId: string) =>
    unwrap<DirectorsReportRecipientDto[]>(await apiClient.get("/directors-reports/recipients", { params: { companyId } })),
  saveRecipient: async (companyId: string, dto: Omit<DirectorsReportRecipientDto, "id" | "companyId" | "lastSuccessfulDeliveryAt" | "lastFailedDeliveryReason">, id?: string) =>
    unwrap<DirectorsReportRecipientDto>(
      id
        ? await apiClient.put(`/directors-reports/recipients/${id}`, dto, { params: { companyId } })
        : await apiClient.post("/directors-reports/recipients", dto, { params: { companyId } })
    ),
  deleteRecipient: async (companyId: string, recipientId: string) =>
    unwrap<boolean>(await apiClient.delete(`/directors-reports/recipients/${recipientId}`, { params: { companyId } })),
  getReports: async (companyId: string) =>
    unwrap<DirectorsReportSummaryDto[]>(await apiClient.get("/directors-reports", { params: { companyId, pageSize: 50 } })),
  getSalesPerformance: async (companyId: string, asOf?: string) =>
    unwrap<SalesPerformanceReportDto>(await apiClient.get("/reports/sales/performance", { params: { companyId, asOf } })),
  getCompanyPerformance: async (companyId: string, asOf?: string) =>
    unwrap<CompanyPerformanceReportDto>(await apiClient.get("/reports/company-performance", { params: { companyId, asOf } })),
  getSeedlingsMovement: async (companyId: string, from: string, through: string) =>
    unwrap<SeedlingsMovementReportDto>(await apiClient.get(
      "/reports/seedlings-movement", { params: { companyId, from, through } }
    )),
  getReport: async (reportId: string) =>
    unwrap<DirectorsReportDetailDto>(await apiClient.get(`/directors-reports/${reportId}`)),
  generate: async (dto: {
    companyId: string;
    scheduleId?: string | null;
    reportType: DirectorsReportFrequency;
    periodStart: string;
    periodEnd: string;
    enabledSections: DirectorsReportSection[];
    compareWithPreviousPeriod: boolean;
    deliverEmail: boolean;
    asOf?: string;
    reportFrom?: string;
    reportThrough?: string;
  }, idempotencyKey: string) => unwrap<DirectorsReportDetailDto>(await apiClient.post("/directors-reports/generate", dto, {
    headers: { "Idempotency-Key": idempotencyKey },
  })),
  send: async (reportId: string, recipientScheduleId?: string) =>
    unwrap<DirectorsReportSendResultDto>(await apiClient.post(
      `/directors-reports/${reportId}/send`,
      undefined,
      { params: { recipientScheduleId } }
    )),
  download: async (reportId: string) => {
    const response = await apiClient.get(`/directors-reports/${reportId}/download`, {
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    });
    const fileName = parseFileName(response.headers["content-disposition"]) ?? `directors-report-${reportId}.pdf`;
    triggerBlobDownload(response.data, fileName);
  },
};
